type UploadFailureState = {
  expiresAt: number
  message: string
}

type ChunkUploadOptions = {
  expectedChunkSize?: number
  uploadId?: string
}

type UploadFormDataInChunksOptions = {
  completeUploadRequest: (formData: FormData) => Promise<any>
  fileNameFieldName?: string
  fileFieldName: string
  formData: FormData
  rollbackChunksRequest: (counter: number, fileName: string, uploadId?: string) => Promise<any>
  uploadChunksRequest: (
    chunk: Blob,
    counter: number,
    fileName: string,
    options?: ChunkUploadOptions
  ) => Promise<any>
}

const CHUNK_SIZE = 128 * 1024
const FAILURE_WINDOW_MS = 5000
const DEFAULT_UPLOAD_FAILURE_MESSAGE =
  'File upload could not be completed because the connection was interrupted or some chunks are missing. Please retry.'

let pendingUploadFailure: UploadFailureState | null = null

const getActiveFailureState = () => {
  if (!pendingUploadFailure) {
    return null
  }

  if (pendingUploadFailure.expiresAt < Date.now()) {
    pendingUploadFailure = null
    return null
  }

  return pendingUploadFailure
}

export const registerUploadFailureNotification = (message?: string) => {
  pendingUploadFailure = {
    expiresAt: Date.now() + FAILURE_WINDOW_MS,
    message: message || DEFAULT_UPLOAD_FAILURE_MESSAGE,
  }
}

export const consumeUploadErrorOverride = () => {
  return getActiveFailureState()?.message || null
}

export const consumeUploadSuccessSuppression = () => {
  const state = getActiveFailureState()
  if (!state) {
    return false
  }

  pendingUploadFailure = null
  return true
}

export const clearUploadFailureNotification = () => {
  pendingUploadFailure = null
}

export const getUploadFailureMessage = (error: any) => {
  const responseData = error?.response?.data

  if (typeof responseData?.error === 'string' && responseData.error.trim()) {
    return responseData.error
  }

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData
  }

  if (typeof error?.message === 'string' && error.message.trim() && error.message !== 'Network Error') {
    return error.message
  }

  if (error?.response?.status === 413) {
    return 'File upload failed because a chunk exceeded the server limit. Please try again.'
  }

  return DEFAULT_UPLOAD_FAILURE_MESSAGE
}

export const uploadFormDataInChunks = async ({
  completeUploadRequest,
  fileNameFieldName = 'fileName',
  fileFieldName,
  formData,
  rollbackChunksRequest,
  uploadChunksRequest,
}: UploadFormDataInChunksOptions) => {
  const uploadedFile = formData.get(fileFieldName)
  if (!(uploadedFile instanceof File)) {
    throw new Error('Uploaded file not found in form data.')
  }

  const fileName = String(formData.get(fileNameFieldName) || uploadedFile.name)
  const uploadId = window.crypto.randomUUID()
  const chunkCount = Math.max(1, Math.ceil(uploadedFile.size / CHUNK_SIZE))
  let uploadedChunkCount = 0
  let chunkStart = 0
  let chunkEnd = CHUNK_SIZE

  try {
    for (let counter = 1; counter <= chunkCount; counter++) {
      const chunk = uploadedFile.slice(chunkStart, chunkEnd)

      await uploadChunksRequest(chunk, counter, fileName, {
        expectedChunkSize: chunk.size,
        uploadId,
      })

      uploadedChunkCount = counter
      chunkStart = chunkEnd
      chunkEnd += CHUNK_SIZE
    }

    const finalizeFormData = new FormData()
    formData.forEach((value, key) => {
      if (key === fileFieldName || value instanceof File) {
        return
      }

      finalizeFormData.append(key, value)
    })

    if (!finalizeFormData.get(fileNameFieldName)) {
      finalizeFormData.append(fileNameFieldName, fileName)
    }

    finalizeFormData.append('chunkNumber', chunkCount.toString())
    finalizeFormData.append('fileSize', uploadedFile.size.toString())
    finalizeFormData.append('uploadId', uploadId)

    const response = await completeUploadRequest(finalizeFormData)
    if (response?.data?.isSuccess === false) {
      throw new Error(response?.data?.error || DEFAULT_UPLOAD_FAILURE_MESSAGE)
    }

    clearUploadFailureNotification()
    return response
  } catch (error) {
    if (uploadedChunkCount > 0) {
      try {
        await rollbackChunksRequest(uploadedChunkCount, fileName, uploadId)
      } catch (rollbackError) {
        console.error(rollbackError)
      }
    }

    registerUploadFailureNotification(getUploadFailureMessage(error))
    throw error
  }
}
